/**
 * @fileoverview Complete inspection case and case-event schemas for the
 *               complex-inspections contract bundle (document 13).
 * @module import-core/contracts/complex-inspections/caseSchemas
 *
 * Responsibilities:
 * - Define the complete `cases` document schema including events, template
 *   snapshots, responses, nested violations, evidence, responsible-party
 *   state, complex visit objects, report links, and billing source refs.
 * - Enforce event-level invariants: `complexVisitManifest` and
 *   `complexVisitReference` are mutually exclusive; manifest participant
 *   `(caseId,eventIndex)` and `entityId` are each unique.
 *
 * Dependencies:
 * - `zod` v3; `./sharedSchemas`; `./contactSchemas` (case links, snapshots).
 *
 * Architecture:
 * - Persisted-boundary schemas. Cases/events stay top-level authoritative —
 *   manifests store references only; no case embeds another case and no event
 *   embeds another event.
 *
 * Notes:
 * - `childCases` is explicitly forbidden on new complex writes; the strict
 *   schema rejects it. Legacy `isParentCase`/`parentCaseId`/`linkedEntities`
 *   remain readable compatibility fields only.
 */

import { z } from 'zod';
import {
  objectIdLikeSchema,
  templateIdLikeSchema,
  formIdLikeSchema,
  fieldIdStringSchema,
  isoDateLikeSchema,
  lookupReferenceSchema,
  legacySourceSchema,
  entityCategorySchema,
  auditFieldsSchema,
  tombstoneFieldsSchema,
} from './sharedSchemas';
import {
  caseContactLinkSchema,
  eventResponsiblePartySnapshotSchema,
  responsiblePartyStatusSchema,
} from './contactSchemas';
import {
  deriveComplexVisitIndexProjection,
  type ComplexVisitIndexProjection,
} from './complexVisitPlanning';

// ---------------------------------------------------------------------------
// Shared enums
// ---------------------------------------------------------------------------

export const caseStatusSchema = z.enum(['notStarted', 'active', 'closedOut', 'caseCompleted']);
export const eventCategorySchema = z.enum(['inspection', 'permit', 'generalActivity', 'adminAction']);
export const eventStatusSchema = z.enum(['scheduled', 'inProgress', 'completed', 'closedOut']);
export const complexParticipationModeSchema = z.enum(['complex-governed', 'standalone']);

// ---------------------------------------------------------------------------
// Complex visit objects (document 13 §3)
// ---------------------------------------------------------------------------

/**
 * @contract complexVisitParticipantRefSchema
 * Purpose: One participant pointer inside a root visit manifest. Carries
 *   identity + display snapshots only — never statuses, responses, or
 *   contact/billing/report payloads.
 */
export const complexVisitParticipantRefSchema = z
  .object({
    caseId: objectIdLikeSchema,
    eventIndex: z.number().int(),
    entityId: objectIdLikeSchema,
    entityCategory: entityCategorySchema,
    entityName: z.string(),
    entityAddress: z.string().optional(),
    participationMode: complexParticipationModeSchema,
  })
  .strict();

/**
 * @contract complexVisitManifestSchema
 * Purpose: Root manifest on the root event — the exact participant event
 *   references for one complex visit. Excludes the root event itself.
 * Rules: `(caseId,eventIndex)` pairs unique; `entityId` unique.
 */
export const complexVisitManifestSchema = z
  .object({
    schemaVersion: z.literal(1),
    visitId: objectIdLikeSchema,
    groupId: objectIdLikeSchema,
    groupName: z.string(),
    anchorEntityId: objectIdLikeSchema,
    anchorEntityName: z.string(),
    rootCaseId: objectIdLikeSchema,
    rootEventIndex: z.number().int(),
    visitType: z.enum(['initial', 'reinspection']),
    participantEvents: z.array(complexVisitParticipantRefSchema),
    createdAt: isoDateLikeSchema,
    createdBy: objectIdLikeSchema,
  })
  .strict()
  .superRefine((manifest, ctx) => {
    const caseEventKeys = new Set<string>();
    const entityIds = new Set<string>();
    manifest.participantEvents.forEach((ref, index) => {
      // Canonicalize 24-hex ids to lowercase so case/entity ids differing
      // only by letter case are duplicates.
      const canon = (value: unknown) => {
        const raw = String(value);
        return /^[a-f\d]{24}$/i.test(raw) ? raw.toLowerCase() : raw;
      };
      const caseEventKey = `${canon(ref.caseId)}:${ref.eventIndex}`;
      if (caseEventKeys.has(caseEventKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['participantEvents', index],
          message: 'participant (caseId,eventIndex) must be unique within a manifest',
        });
      }
      caseEventKeys.add(caseEventKey);
      const entityKey = canon(ref.entityId);
      if (entityIds.has(entityKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['participantEvents', index, 'entityId'],
          message: 'participant entityId must be unique within a manifest',
        });
      }
      entityIds.add(entityKey);
    });
  });

/**
 * @contract complexVisitReferenceSchema
 * Purpose: Participant event's single reference back to the root manifest.
 *   `participationMode` is an immutable snapshot for that visit.
 */
export const complexVisitReferenceSchema = z
  .object({
    schemaVersion: z.literal(1),
    visitId: objectIdLikeSchema,
    groupId: objectIdLikeSchema,
    rootCaseId: objectIdLikeSchema,
    rootEventIndex: z.number().int(),
    participationMode: complexParticipationModeSchema,
  })
  .strict();

// ---------------------------------------------------------------------------
// Report generation + artifact links (document 13 §4)
// ---------------------------------------------------------------------------

export const reportGenerationFailureReasonSchema = z.enum([
  'noConfiguredReportTemplate',
  'missingReference',
  'notFound',
  'deleted',
  'unpublished',
  'wrongScope',
  'ambiguous',
  'invalidDocumentType',
  'invalidReportDataScope',
  'participantsIncomplete',
  'reportJobTransportFailed',
  'attachmentsNotReady',
  'recipientResolutionFailed',
]);

export const reportGenerationOutcomeSchema = z
  .object({
    status: z.enum(['queued', 'pendingApproval', 'awaitingResponsibleParty', 'failed']),
    reason: reportGenerationFailureReasonSchema.optional(),
    reportTemplateId: templateIdLikeSchema.optional(),
    evaluatedAt: isoDateLikeSchema,
  })
  .strict();

export const eventReportArtifactLinkSchema = z
  .object({
    schemaVersion: z.literal(1),
    reportId: objectIdLikeSchema,
    reportDataScope: z.enum(['singleCase', 'complexVisit']),
    correspondencePurpose: z.enum(['standalone', 'defaultComplex', 'oneOffEntity']),
    isDefault: z.boolean(),
    visitId: objectIdLikeSchema.optional(),
    groupId: objectIdLikeSchema.optional(),
    reportNumber: z.string().optional(),
    templateName: z.string().optional(),
    status: z.enum(['pending', 'generating', 'ready', 'sent', 'delivered', 'failed']),
    generatedAt: isoDateLikeSchema.optional(),
    sentAt: isoDateLikeSchema.optional(),
    deliveredAt: isoDateLikeSchema.optional(),
  })
  .strict()
  .superRefine((link, ctx) => {
    // Doc 13 §4: `complexVisit` scope iff purpose 'defaultComplex' — requires
    // visitId/groupId. `singleCase` purposes ('standalone'/'oneOffEntity')
    // conservatively forbid visit/group references.
    if (link.correspondencePurpose === 'defaultComplex') {
      if (link.reportDataScope !== 'complexVisit') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reportDataScope'],
          message: "correspondencePurpose 'defaultComplex' requires reportDataScope 'complexVisit'",
        });
      }
      for (const key of ['visitId', 'groupId'] as const) {
        if (link[key] === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [key],
            message: `defaultComplex artifact link requires ${key}`,
          });
        }
      }
    } else {
      if (link.reportDataScope !== 'singleCase') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reportDataScope'],
          message: `correspondencePurpose '${link.correspondencePurpose}' requires reportDataScope 'singleCase'`,
        });
      }
      for (const key of ['visitId', 'groupId'] as const) {
        if (link[key] !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [key],
            message: `singleCase artifact link must not carry ${key}`,
          });
        }
      }
    }
  });

export const legacyEventReportProjectionSchema = z
  .object({
    reportId: z.string().optional(),
    templateName: z.string().optional(),
    status: z.string().optional(),
    generatedAt: z.string().optional(),
    pdfUrl: z.string().optional(),
    sentAt: z.string().optional(),
    deliveredAt: z.string().optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Responses, violations, evidence (document 13 §5)
// ---------------------------------------------------------------------------

export const MISSING_RESPONSE_FORM_ID = '__missing_response__';

export const attachmentRefSchema = z
  .object({
    _id: objectIdLikeSchema,
    parentType: z.string(),
    parentId: objectIdLikeSchema,
    parentPath: z.string().optional(),
    kind: z.string(),
    fileName: z.string().optional(),
    blobName: z.string().optional(),
    mimeType: z.string().optional(),
    fileSize: z.number().optional(),
    sha256: z.string().optional(),
    status: z.string().optional(),
    tags: z.array(z.string()).optional(),
    uploadedAt: isoDateLikeSchema.optional(),
    uploadedBy: objectIdLikeSchema.optional(),
    legacySource: z.record(z.unknown()).optional(),
    _deleted: z.boolean().optional(),
  })
  .strict();

const violationCodeRefSchema = z
  .object({
    code: z.string(),
    codeSetId: objectIdLikeSchema,
    description: z.string().optional(),
  })
  .strict();

const violationOccurrenceSchema = z
  .object({
    _id: objectIdLikeSchema,
    location: z.string().optional(),
    zone: z.string().optional(),
    correctiveActions: z.string().optional(),
    notes: z.string().optional(),
    status: z.string(),
    correctionStatus: z
      .enum(['pending', 'inProgress', 'repaired', 'exception', 'extensionGranted', 'retracted'])
      .optional(),
    fixedInEventIndex: z.number().int().optional(),
    fixedAt: isoDateLikeSchema.optional(),
    fixedBy: objectIdLikeSchema.optional(),
    fixedNotes: z.string().optional(),
    correctionDeadline: isoDateLikeSchema.optional(),
    correctionNotes: z.string().optional(),
    correctionCompletedAt: isoDateLikeSchema.optional(),
    correctionVerifiedAt: isoDateLikeSchema.optional(),
    correctionVerifiedBy: objectIdLikeSchema.optional(),
    correctionVerifiedInEventIndex: z.number().int().optional(),
    exceptionGrantedAt: isoDateLikeSchema.optional(),
    exceptionGrantedBy: objectIdLikeSchema.optional(),
    exceptionReason: z.string().optional(),
    exceptionGrantedInEventIndex: z.number().int().optional(),
    extensions: z
      .array(
        z
          .object({
            grantedAt: isoDateLikeSchema,
            grantedBy: objectIdLikeSchema,
            grantedInEventIndex: z.number().int(),
            reason: z.string(),
            previousDeadline: isoDateLikeSchema,
            newDeadline: isoDateLikeSchema,
          })
          .strict(),
      )
      .optional(),
    discoveredInEventIndex: z.number().int(),
    discoveredAt: isoDateLikeSchema,
    discoveredBy: objectIdLikeSchema,
    attachments: z.array(attachmentRefSchema),
    _deleted: z.boolean().optional(),
  })
  .strict();

const violationSchema = z
  .object({
    _id: objectIdLikeSchema,
    codes: z.array(violationCodeRefSchema),
    code: z.string().optional(),
    description: z.string().optional(),
    severity: z.string(),
    status: z.string(),
    source: z.enum(['question', 'additional']),
    relatedQuestionId: z.string().optional(),
    discoveredInEventIndex: z.number().int(),
    discoveredAt: isoDateLikeSchema.optional(),
    discoveredBy: objectIdLikeSchema.optional(),
    closedInEventIndex: z.number().int().optional(),
    occurrences: z.array(violationOccurrenceSchema),
    legacySource: z.record(z.unknown()).optional(),
  })
  .strict();

const responseTransitionSchema = z
  .object({
    eventIndex: z.number().int(),
    changedAt: isoDateLikeSchema,
    changedBy: objectIdLikeSchema,
    kind: z.enum(['value', 'isNA', 'isHidden']),
    previousValue: z.unknown().optional(),
    newValue: z.unknown().optional(),
    previousIsNA: z.boolean().optional(),
    newIsNA: z.boolean().optional(),
    previousIsHidden: z.boolean().optional(),
    newIsHidden: z.boolean().optional(),
    reason: z.string().optional(),
    triggeredByRuleId: z.string().optional(),
    triggeredByFieldId: z.string().optional(),
    sourceId: z.union([z.string(), z.number()]).optional(),
  })
  .strict();

const visibilityReasonSchema = z.enum(['initial', 'rule', 'crossForm', 'manual', 'other']);

const formVisibilityStateSchema = z
  .object({
    formId: z.string(),
    isHidden: z.boolean(),
    hiddenReason: visibilityReasonSchema.optional(),
    hiddenByRuleId: z.string().optional(),
    lastChangedAt: isoDateLikeSchema.optional(),
    lastChangedInEventIndex: z.number().int().optional(),
  })
  .strict();

const sectionVisibilityStateSchema = z
  .object({
    sectionId: z.string(),
    formId: z.string(),
    isHidden: z.boolean(),
    hiddenReason: visibilityReasonSchema.optional(),
    hiddenByRuleId: z.string().optional(),
    lastChangedAt: isoDateLikeSchema.optional(),
    lastChangedInEventIndex: z.number().int().optional(),
  })
  .strict();

const questionResponseSchema = z
  .object({
    value: z.unknown().optional(),
    formId: z.string(),
    sectionId: z.string().optional(),
    fieldId: z.string(),
    widgetType: z.string(),
    questionText: z.string().optional(),
    isNA: z.boolean(),
    naSetAt: isoDateLikeSchema.optional(),
    naSetBy: objectIdLikeSchema.optional(),
    isHidden: z.boolean(),
    hiddenReason: visibilityReasonSchema.optional(),
    parentFormIsHidden: z.boolean().optional(),
    parentSectionIsHidden: z.boolean().optional(),
    violation: violationSchema.optional(),
    dataBoundSource: z
      .object({
        collection: z.string(),
        field: z.string(),
        capturedValue: z.unknown().optional(),
        wasModifiedByInspector: z.boolean(),
      })
      .strict()
      .optional(),
    valueSource: z
      .object({
        kind: z.enum(['user', 'dataBound', 'ruleComputed']),
        ruleId: z.string().optional(),
        ruleVersion: z.string().optional(),
        computedAt: isoDateLikeSchema.optional(),
        boundField: z.string().optional(),
      })
      .strict()
      .optional(),
    attachments: z.array(attachmentRefSchema),
    transitions: z.array(responseTransitionSchema),
    legacyFlags: z.record(z.unknown()).optional(),
    updatedAt: isoDateLikeSchema.optional(),
    updatedBy: objectIdLikeSchema.optional(),
  })
  .strict();

/**
 * @contract isViolationOnlyResponse
 * Purpose: Identifies a response object created by a nested violation write
 *   whose parent question response was absent.
 * Fields: A response-like object; its nested `violation` is the only signal used.
 * Rules: All required question fields must be absent, while `violation` must be present.
 * Sync: The pull/push boundary uses this to repair migration-created stubs.
 */
export function isViolationOnlyResponse(value: unknown): value is { violation: unknown } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const response = value as Record<string, unknown>;
  return response.violation !== undefined
    && response.formId === undefined
    && response.fieldId === undefined
    && response.widgetType === undefined
    && response.isNA === undefined
    && response.isHidden === undefined;
}

/**
 * @process completeQuestionResponseDefaults
 * Steps: Fill every required question-response field that is absent from the response; present fields are kept as-is.
 * Inputs: The response key and a partial response object.
 * Outputs: A schema-complete response carrying every existing field, including answers, attachments, and transitions.
 * Failure Handling: None — pure fill of absent keys.
 * Idempotency: REQUIRED — a response that already has required fields keeps its values.
 */
export function completeQuestionResponseDefaults(
  responseKey: string,
  response: Record<string, unknown>,
): Record<string, unknown> {
  return {
    value: null,
    formId: MISSING_RESPONSE_FORM_ID,
    fieldId: responseKey,
    widgetType: 'unknown',
    isNA: false,
    isHidden: false,
    attachments: [],
    transitions: [],
    valueSource: { kind: 'user' as const },
    ...response,
  };
}

/**
 * @process completeViolationOnlyResponse
 * Steps: Copy the stub and fill only the required question-response fields.
 * Inputs: The response key and its violation-only value.
 * Outputs: A schema-complete response that retains the original violation.
 * Failure Handling: Returns the input unchanged when it is not a violation-only stub.
 * Idempotency: REQUIRED — a response that already has required fields is unchanged.
 */
export function completeViolationOnlyResponse(
  responseKey: string,
  value: unknown,
): unknown {
  if (!isViolationOnlyResponse(value)) return value;
  return completeQuestionResponseDefaults(responseKey, value);
}

const ruleEffectSchema = z
  .object({
    ruleId: z.string(),
    ruleVersion: z.string().optional(),
    effect: z.enum(['hide', 'show', 'computeValue', 'triggerViolation', 'setRequired', 'setNA']),
    targetType: z.enum(['form', 'section', 'field']),
    targetId: z.string(),
    triggeredByFieldId: z.string().optional(),
    previousState: z.unknown().optional(),
    newState: z.unknown().optional(),
    evaluatedAt: isoDateLikeSchema,
  })
  .strict();

const eventResponseSnapshotSchema = z
  .object({
    eventIndex: z.number().int(),
    responses: z.record(z.string(), questionResponseSchema),
    formStates: z.record(z.string(), formVisibilityStateSchema).optional(),
    sectionStates: z.record(z.string(), sectionVisibilityStateSchema).optional(),
    ruleEffects: z.array(ruleEffectSchema).optional(),
    transitionsCount: z.number().int().optional(),
    capturedAt: isoDateLikeSchema,
    capturedBy: objectIdLikeSchema,
  })
  .strict();

const eventSignatureSchema = z
  .object({
    _id: objectIdLikeSchema,
    role: z.enum(['inspector', 'business', 'witness', 'other']),
    roleLabel: z.string().optional(),
    signerName: z.string().optional(),
    signerTitle: z.string().optional(),
    attachmentRef: attachmentRefSchema,
    capturedAt: isoDateLikeSchema,
    capturedBy: objectIdLikeSchema,
    capturedInEventIndex: z.number().int(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Billing source (document 13 §6 / document 14 §8)
// ---------------------------------------------------------------------------

/**
 * @contract inspectionBillingSourceRefSchema
 * Purpose: Deterministic billing grouping reference. `complex-visit:<visitId>`
 *   for governed contributors; `case-event:<caseId>:<eventIndex>` for
 *   standalone events. No invoice collection is invented.
 */
export const inspectionBillingSourceRefSchema = z
  .object({
    billingScope: z.enum(['complexVisit', 'standaloneEvent']),
    billingGroupKey: z.string(),
    visitId: objectIdLikeSchema.optional(),
    groupId: objectIdLikeSchema.optional(),
    rootCaseId: objectIdLikeSchema.optional(),
    rootEventIndex: z.number().int().optional(),
    caseId: objectIdLikeSchema.optional(),
    eventIndex: z.number().int().optional(),
  })
  .strict()
  .superRefine((source, ctx) => {
    const fail = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    if (source.billingScope === 'complexVisit') {
      // Requires the full root reference set and the exact derived key;
      // standalone qualifiers are forbidden (doc 13 §6 / doc 14 §8).
      for (const key of ['visitId', 'groupId', 'rootCaseId', 'rootEventIndex'] as const) {
        if (source[key] === undefined) {
          fail([key], `complexVisit billing source requires ${key}`);
        }
      }
      for (const key of ['caseId', 'eventIndex'] as const) {
        if (source[key] !== undefined) {
          fail([key], `complexVisit billing source must not carry ${key}`);
        }
      }
      if (
        source.visitId !== undefined &&
        source.billingGroupKey !== `complex-visit:${String(source.visitId)}`
      ) {
        fail(['billingGroupKey'], "complexVisit billingGroupKey must equal 'complex-visit:' + visitId");
      }
    }
    if (source.billingScope === 'standaloneEvent') {
      if (source.caseId === undefined) fail(['caseId'], 'standaloneEvent billing source requires caseId');
      if (source.eventIndex === undefined) fail(['eventIndex'], 'standaloneEvent billing source requires eventIndex');
      for (const key of ['visitId', 'groupId', 'rootCaseId', 'rootEventIndex'] as const) {
        if (source[key] !== undefined) {
          fail([key], `standaloneEvent billing source must not carry ${key}`);
        }
      }
      if (
        source.caseId !== undefined &&
        source.eventIndex !== undefined &&
        source.billingGroupKey !== `case-event:${String(source.caseId)}:${source.eventIndex}`
      ) {
        fail(['billingGroupKey'], "standaloneEvent billingGroupKey must equal 'case-event:' + caseId + ':' + eventIndex");
      }
    }
  });

/**
 * @contract externalBillingReferenceSchema
 * Purpose: Opaque source-labeled identity/status returned by IntelliCalc or RecoveryHub.
 * Fields: Source system, external inspection identity, optional calculation/invoice/import identities, status, and observation timestamps.
 * Rules: FireSquirrel never derives these identifiers or stores invoice amounts/line items here; observedAt is required.
 * Sync: Server-authored event billing references synchronize through the case document.
 */
export const externalBillingReferenceSchema = z
  .object({
    schemaVersion: z.literal(1),
    sourceSystem: z.enum(['intellicalc', 'recoveryhub']),
    externalInspectionId: z.string().min(1),
    calculationRecordId: z.string().min(1).optional(),
    invoiceId: z.string().min(1).optional(),
    invoiceDisplayId: z.string().min(1).optional(),
    importedSourceId: z.string().min(1).optional(),
    statusSnapshot: z.string().optional(),
    observedAt: isoDateLikeSchema,
    sourceUpdatedAt: isoDateLikeSchema.optional(),
  })
  .strict();

export const eventBillingSchema = z
  .object({
    inspectionFee: z.number().optional(),
    reinspectionFee: z.number().optional(),
    travelTime: z.number().optional(),
    calculatedFee: z.number().optional(),
    feeWaived: z.boolean().optional(),
    feeWaivedReason: z.string().optional(),
    source: inspectionBillingSourceRefSchema,
    externalReferences: z.array(externalBillingReferenceSchema).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// CaseEvent (document 13 §7)
// ---------------------------------------------------------------------------

/**
 * @contract caseEventSchema
 * Purpose: Complete event entry inside a case. An event may carry a root
 *   `complexVisitManifest`, a participant `complexVisitReference`, or neither
 *   — never both (enforced by refinement).
 */
export const caseEventSchema = z
  .object({
    eventIndex: z.number().int(),
    eventCategory: eventCategorySchema,
    eventType: lookupReferenceSchema,
    inspectionType: lookupReferenceSchema.optional(),

    reinspectionNumber: z.number().int().optional(),
    createdByEventIndex: z.number().int().optional(),
    extensionHonored: z.boolean().optional(),
    extensionHonoredForOccurrenceIds: z.array(objectIdLikeSchema).optional(),

    primaryInspectorId: objectIdLikeSchema,
    primaryInspectorName: z.string(),
    secondaryInspectorIds: z.array(objectIdLikeSchema).optional(),
    secondaryInspectorNames: z.array(z.string()).optional(),

    status: eventStatusSchema,
    createdAt: isoDateLikeSchema,
    createdBy: objectIdLikeSchema,

    scheduledDate: isoDateLikeSchema.optional(),
    scheduledStart: isoDateLikeSchema.optional(),
    scheduledEnd: isoDateLikeSchema.optional(),
    scheduledBy: objectIdLikeSchema.optional(),
    startedAt: isoDateLikeSchema.optional(),
    completedAt: isoDateLikeSchema.optional(),
    closedOutAt: isoDateLikeSchema.optional(),
    closedOutBy: objectIdLikeSchema.optional(),
    closedOutReason: lookupReferenceSchema.optional(),

    details: z.record(z.unknown()).optional(),
    internalNotes: z.string().optional(),
    externalNotes: z.string().optional(),

    responseSnapshot: eventResponseSnapshotSchema.optional(),
    signatures: z.array(eventSignatureSchema).optional(),
    photos: z.array(attachmentRefSchema).optional(),

    responsiblePartyContactId: objectIdLikeSchema.optional(),
    responsiblePartySnapshot: eventResponsiblePartySnapshotSchema.optional(),
    responsiblePartyStatus: responsiblePartyStatusSchema.optional(),

    complexVisitManifest: complexVisitManifestSchema.optional(),
    complexVisitReference: complexVisitReferenceSchema.optional(),

    reportGeneration: reportGenerationOutcomeSchema.optional(),
    reportArtifacts: z.array(eventReportArtifactLinkSchema).optional(),
    report: legacyEventReportProjectionSchema.optional(),

    billing: eventBillingSchema.optional(),

    syncStatus: z.enum(['synced', 'pending', 'conflict']),
    lastSyncedAt: isoDateLikeSchema.optional(),
    deviceId: z.string().optional(),
    legacySource: z.record(z.unknown()).optional(),
  })
  .strict()
  .superRefine((event, ctx) => {
    if (event.complexVisitManifest !== undefined && event.complexVisitReference !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['complexVisitReference'],
        message: 'an event may carry a manifest or a participant reference, never both',
      });
    }
    if (event.eventCategory === 'inspection' && event.inspectionType === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['inspectionType'],
        message: "inspectionType is required when eventCategory is 'inspection'",
      });
    }
  });

// ---------------------------------------------------------------------------
// Template snapshot (document 13 §8)
// ---------------------------------------------------------------------------

const templateSnapshotFieldSchema = z
  .object({
    // Client snapshots spread the source field first, so both `id` (builder
    // key) and `fieldId` (canonical key) are present. Conductor code resolves
    // `id ?? fieldId` — dropping `id` at the sync boundary breaks answer matching.
    id: fieldIdStringSchema.optional(),
    fieldId: fieldIdStringSchema,
    type: z.string(),
    label: z.string(),
    helpText: z.string().optional(),
    required: z.boolean().optional(),
    defaultValue: z.unknown().optional(),
    naEnabled: z.boolean().optional(),
    options: z.array(z.unknown()).optional(),
    violationMappings: z.array(z.unknown()).optional(),
    sectionId: z.string().optional(),
    initialVisibility: z.enum(['shown', 'hidden']).optional(),
    extras: z.record(z.unknown()).optional(),
  })
  .strict();

const templateSnapshotFormSchema = z
  .object({
    formId: formIdLikeSchema,
    formName: z.string(),
    formVersion: z.number(),
    description: z.string().optional(),
    fields: z.array(templateSnapshotFieldSchema),
    layout: z
      .object({
        sections: z
          .array(
            z
              .object({
                id: z.string(),
                title: z.string().optional(),
                description: z.string().optional(),
                columns: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
                order: z.number().optional(),
                fieldIds: z.array(z.string()).optional(),
                initialVisibility: z.enum(['shown', 'hidden']).optional(),
              })
              .strict(),
          )
          .optional(),
      })
      .strict()
      .optional(),
    rules: z.array(z.unknown()).optional(),
    formKind: z.string().optional(),
    moduleConfig: z.record(z.unknown()).optional(),
  })
  .strict();

/**
 * @contract templateSnapshotSchema
 * Purpose: Strict template snapshot shape embedded on a case — exact allowed
 *   form/field/layout keys only; `reportConfig` passes through when defined.
 * Rules: exported so `captureTemplateSnapshot` (tenant-api) can validate the
 *   snapshot it emits before returning — the schema is the authority, not
 *   the capture code.
 * Sync: embedded on schemaVersion:1 cases and snapshotHistory entries.
 */
export const templateSnapshotSchema = z
  .object({
    forms: z.array(templateSnapshotFormSchema),
    parameters: z
      .object({
        selfCertificationEnabled: z.boolean(),
        generateInspectionReport: z.boolean(),
        requireInspectorSignature: z.boolean(),
        requireResponsiblePartySignature: z.boolean(),
        requireResponsibleParty: z.boolean().optional(),
        autoCreateReinspection: z.boolean(),
        maxReinspections: z.number(),
        reinspectionSchedule: z.array(
          z
            .object({
              reinspectionNumber: z.number().int(),
              dueDays: z.number(),
              feeMultiplier: z.number().optional(),
            })
            .strict(),
        ),
        allowExtensions: z.boolean(),
        allowExceptions: z.boolean(),
        enableSeverity: z.boolean(),
      })
      .strict(),
    reportConfig: z.unknown().optional(),
  })
  .strict();

const snapshotHistoryEntrySchema = z
  .object({
    replacedAtEventIndex: z.number().int(),
    replacedAt: isoDateLikeSchema,
    snapshot: templateSnapshotSchema,
  })
  .strict();

// ---------------------------------------------------------------------------
// InspectionCase document (document 13 §9)
// ---------------------------------------------------------------------------

/**
 * @contract inspectionCaseDocumentSchema
 * Purpose: Complete target `cases` document (schemaVersion 1). Events remain
 *   authoritative; `complexVisitIds`/`complexGroupIds`/`complexRootVisitIds`
 *   are materialized query projections. `childCases` is forbidden on target
 *   writes (strict schema rejects it).
 */
export const inspectionCaseDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),

    entityId: objectIdLikeSchema,
    entityCategory: entityCategorySchema,
    entityName: z.string(),
    entityAddress: z.string().optional(),

    inspectionAccountId: objectIdLikeSchema,
    inspectionAccountName: z.string().optional(),

    templateId: templateIdLikeSchema,
    templateName: z.string(),
    templateVersion: z.number(),
    templateSnapshot: templateSnapshotSchema,
    snapshotHistory: z.array(snapshotHistoryEntrySchema).optional(),

    caseType: lookupReferenceSchema,
    caseNumber: z.string().optional(),
    canonicalCaseNumber: z.string().optional(),
    caseSequence: z.number().optional(),
    fiscalYear: z.number(),

    caseStatus: caseStatusSchema,
    closedOutReason: lookupReferenceSchema.optional(),
    closedOutNotes: z.string().optional(),
    closedOutAt: isoDateLikeSchema.optional(),
    closedOutBy: objectIdLikeSchema.optional(),
    fiscalYearCarryForwardEligible: z.boolean().optional(),

    isArchived: z.boolean(),
    archivedAt: isoDateLikeSchema.optional(),
    archivedBy: objectIdLikeSchema.optional(),

    internalNotes: z.string().optional(),
    externalNotes: z.string().optional(),

    contacts: z.array(caseContactLinkSchema).optional(),

    responses: z.record(z.string(), questionResponseSchema),
    formStates: z.record(z.string(), formVisibilityStateSchema).optional(),
    sectionStates: z.record(z.string(), sectionVisibilityStateSchema).optional(),

    customFieldValues: z.record(z.unknown()).optional(),
    wildlandData: z.record(z.unknown()).optional(),
    fireCodeData: z.record(z.unknown()).optional(),
    hydrantData: z.record(z.unknown()).optional(),
    homeSafetyData: z.record(z.unknown()).optional(),

    events: z.array(caseEventSchema),

    billingTotal: z
      .object({
        totalFees: z.number(),
        paidAmount: z.number(),
        outstandingBalance: z.number(),
        invoiceNumber: z.string().optional(),
        invoiceDate: isoDateLikeSchema.optional(),
        paymentStatus: z.enum(['unpaid', 'partial', 'paid']),
      })
      .strict()
      .optional(),

    parcelRetired: z
      .object({
        parcelId: objectIdLikeSchema,
        apn: z.string().optional(),
        reason: z.enum(['import_removed', 'parcel_merged', 'parcel_split']),
        retiredAt: isoDateLikeSchema,
        importJobId: objectIdLikeSchema.optional(),
        sourceHash: z.string().optional(),
        mergedIntoApn: z.string().optional(),
        splitIntoApns: z.array(z.string()).optional(),
      })
      .strict()
      .optional(),

    activeLock: z
      .object({
        lockedByUserId: objectIdLikeSchema,
        lockedByUserName: z.string(),
        deviceId: z.string().optional(),
        lockedAt: isoDateLikeSchema,
        lockExpiresAt: isoDateLikeSchema,
        activeEventIndex: z.number().int(),
        lockMode: z.enum(['exclusive', 'advisory']),
      })
      .strict()
      .nullable()
      .optional(),

    complexVisitIds: z.array(objectIdLikeSchema).optional(),
    complexGroupIds: z.array(objectIdLikeSchema).optional(),
    complexRootVisitIds: z.array(objectIdLikeSchema).optional(),

    lastActivityDate: isoDateLikeSchema.optional(),

    // Legacy read-compatibility only; new complex writers never populate.
    isParentCase: z.boolean().optional(),
    parentCaseId: objectIdLikeSchema.nullable().optional(),
    linkedEntities: z
      .array(
        z
          .object({
            entityId: objectIdLikeSchema,
            entityName: z.string(),
            entityAddress: z.string().optional(),
            entityCategory: entityCategorySchema,
            role: z.enum(['structure', 'parcel', 'unit', 'outbuilding', 'related']).optional(),
          })
          .strict(),
      )
      .optional(),
    reportGeneration: reportGenerationOutcomeSchema.optional(),
    legacySource: legacySourceSchema.optional(),
    sourceMetadata: z.record(z.unknown()).optional(),
    legacyMigrationAppliedAt: isoDateLikeSchema.optional(),
    attachments: z.array(attachmentRefSchema).optional(),
    permits: z.array(z.record(z.unknown())).optional(),
    generatedReports: z.array(z.record(z.unknown())).optional(),
  })
  .merge(auditFieldsSchema)
  .merge(tombstoneFieldsSchema)
  .strict()
  .superRefine((doc, ctx) => {
    // The events ARRAY index is authoritative — the stored eventIndex field
    // must equal its position (document 16 §4).
    doc.events.forEach((event, index) => {
      if (event.eventIndex !== index) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['events', index, 'eventIndex'],
          message: 'eventIndex must equal the event array index',
        });
      }
    });
    let hasComplexObjects = false;
    doc.events.forEach((event, index) => {
      const manifest = event.complexVisitManifest;
      if (manifest !== undefined || event.complexVisitReference !== undefined) {
        hasComplexObjects = true;
      }
      if (!manifest) return;
      if (String(manifest.rootCaseId) !== String(doc._id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['events', index, 'complexVisitManifest', 'rootCaseId'],
          message: 'root manifest must be hosted by its containing case',
        });
      }
      if (manifest.rootEventIndex !== event.eventIndex) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['events', index, 'complexVisitManifest', 'rootEventIndex'],
          message: 'root manifest rootEventIndex must match its containing event',
        });
      }
    });
    // When any event carries complex visit objects, the materialized index
    // projections must EXACTLY equal the event-derived projection — same
    // order, no extras, no missing (document 16 §4).
    if (hasComplexObjects) {
      const canonical = (value: unknown): string => {
        if (typeof value === 'string') return value.toLowerCase();
        if (
          typeof value === 'object' &&
          value !== null &&
          typeof (value as { toHexString?: unknown }).toHexString === 'function'
        ) {
          return (value as { toHexString: () => string }).toHexString().toLowerCase();
        }
        return String(value);
      };
      const derived = deriveComplexVisitIndexProjection(doc.events);
      const pairs: Array<[keyof ComplexVisitIndexProjection, unknown[] | undefined]> = [
        ['complexVisitIds', doc.complexVisitIds],
        ['complexGroupIds', doc.complexGroupIds],
        ['complexRootVisitIds', doc.complexRootVisitIds],
      ];
      for (const [field, stored] of pairs) {
        const expected = derived[field];
        const actual = stored ?? [];
        const exact =
          actual.length === expected.length &&
          actual.every((value, i) => canonical(value) === canonical(expected[i]));
        if (!exact) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${field} must exactly equal the event-derived complex visit projection`,
          });
        }
      }
    }
  });

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type InspectionCaseDocument = z.infer<typeof inspectionCaseDocumentSchema>;
export type CaseEventDocument = z.infer<typeof caseEventSchema>;
export type ComplexVisitManifest = z.infer<typeof complexVisitManifestSchema>;
export type ComplexVisitReference = z.infer<typeof complexVisitReferenceSchema>;
export type ComplexVisitParticipantRef = z.infer<typeof complexVisitParticipantRefSchema>;
export type EventReportArtifactLink = z.infer<typeof eventReportArtifactLinkSchema>;
export type InspectionBillingSourceRef = z.infer<typeof inspectionBillingSourceRefSchema>;
export type ExternalBillingReference = z.infer<typeof externalBillingReferenceSchema>;
export type EventBilling = z.infer<typeof eventBillingSchema>;
export type ComplexReportGenerationOutcome = z.infer<typeof reportGenerationOutcomeSchema>;
